import { useState, useEffect, useRef, useCallback } from 'react';
import EnvHelper from '../helpers/envHelper';

/**
 * Hook สำหรับใช้ Financial Metrics Worker
 * 
 * @param {Array} transactions - รายการ transactions
 * @param {Object} options - ตัวเลือก
 * @param {boolean} options.enabled - เปิด/ปิดการใช้งาน worker (default: true)
 * @param {number} options.debounceMs - debounce delay (default: 100)
 * @returns {Object} { metrics, isCalculating, error, duration }
 */
export function useFinancialMetricsWorker(transactions = [], options = {}) {
  const {
    enabled = true,
    debounceMs = 100
  } = options;

  const [metrics, setMetrics] = useState({
    credit: 0,
    deposit: 0,
    withdrawal: 0,
    profit: 0,
    balance: 0,
    commission: 0,
    swap: 0
  });
  
  const [isCalculating, setIsCalculating] = useState(false);
  const [error, setError] = useState(null);
  const [duration, setDuration] = useState(0);

  const workerRef = useRef(null);
  const messageIdRef = useRef(0);
  const pendingCallbacksRef = useRef(new Map());
  const debounceTimerRef = useRef(null);

  // สร้าง worker
  useEffect(() => {
    if (!enabled) return;

    try {
      // สร้าง worker จากไฟล์
      const envHelper = EnvHelper.getInstance();
      const workerUrl = new URL('../workers/financialMetrics.worker.js', import.meta.url);
      const fixedUrl = envHelper.fixUrl(workerUrl);
      console.log('[FinancialMetrics] Creating worker with URL:', fixedUrl);
      
      workerRef.current = new Worker(fixedUrl, { type: 'module' });

      // รับ message จาก worker
      workerRef.current.onmessage = (e) => {
        const { type, id, payload } = e.data;

        switch (type) {
          case 'WORKER_READY':
            console.log('💼 Financial Metrics Worker ready');
            break;

          case 'METRICS_RESULT': {
            const callback = pendingCallbacksRef.current.get(id);
            if (callback) {
              callback(null, payload);
              pendingCallbacksRef.current.delete(id);
            }
            break;
          }

          case 'ERROR': {
            const callback = pendingCallbacksRef.current.get(id);
            if (callback) {
              callback(new Error(payload.message), null);
              pendingCallbacksRef.current.delete(id);
            }
            break;
          }

          case 'PONG':
            console.log('💼 Worker health check: OK');
            break;

          default:
            console.warn('Unknown message type from worker:', type);
        }
      };

      // จัดการ error
      workerRef.current.onerror = (err) => {
        console.error('💼 Worker error:', err);
        setError(err.message);
        setIsCalculating(false);
      };

    } catch (err) {
      console.error('Failed to create worker:', err);
      setError(err.message);
    }

    // Cleanup
    return () => {
      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null;
      }
      pendingCallbacksRef.current.clear();
    };
  }, [enabled]);

  // ส่ง message ไปยัง worker
  const postMessage = useCallback((type, payload) => {
    return new Promise((resolve, reject) => {
      if (!workerRef.current) {
        reject(new Error('Worker not initialized'));
        return;
      }

      const id = messageIdRef.current++;
      
      // เก็บ callback
      pendingCallbacksRef.current.set(id, (error, result) => {
        if (error) {
          reject(error);
        } else {
          resolve(result);
        }
      });

      // ส่ง message
      workerRef.current.postMessage({ type, payload, id });
    });
  }, []);

  // คำนวณ metrics
  const calculateMetrics = useCallback(async (txns) => {
    if (!enabled || !workerRef.current) {
      // Fallback: คำนวณใน main thread
      return calculateMetricsSync(txns);
    }

    try {
      setIsCalculating(true);
      setError(null);

      const result = await postMessage('CALCULATE_METRICS', {
        transactions: txns
      });

      setMetrics(result.metrics);
      setDuration(result.duration);
      setIsCalculating(false);

      console.log(`📊 Metrics calculated in worker: ${result.duration.toFixed(2)}ms for ${result.itemCount} items`);

      return result.metrics;
    } catch (err) {
      console.error('Failed to calculate metrics in worker:', err);
      setError(err.message);
      setIsCalculating(false);
      
      // Fallback
      return calculateMetricsSync(txns);
    }
  }, [enabled, postMessage]);

  // คำนวณเมื่อ transactions เปลี่ยน (with debounce)
  useEffect(() => {
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // ถ้าไม่มีข้อมูล reset metrics
    if (!transactions || transactions.length === 0) {
      setMetrics({
        credit: 0,
        deposit: 0,
        withdrawal: 0,
        profit: 0,
        balance: 0,
        commission: 0,
        swap: 0
      });
      setIsCalculating(false);
      return;
    }

    // Set calculating state ทันที เพื่อให้ consumer รู้ว่ากำลังคำนวณ
    setIsCalculating(true);
    setError(null);

    // Debounce calculation
    debounceTimerRef.current = setTimeout(() => {
      calculateMetrics(transactions);
    }, debounceMs);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [transactions, debounceMs, calculateMetrics]);

  // Health check
  const ping = useCallback(async () => {
    if (!workerRef.current) return false;
    
    try {
      await postMessage('PING', {});
      return true;
    } catch {
      return false;
    }
  }, [postMessage]);

  return {
    metrics,
    isCalculating,
    error,
    duration,
    ping,
    calculateMetrics // manual trigger
  };
}

// Fallback: คำนวณใน main thread
function calculateMetricsSync(transactions) {
  if (!transactions || transactions.length === 0) {
    return {
      credit: 0,
      deposit: 0,
      withdrawal: 0,
      profit: 0,
      balance: 0,
      commission: 0,
      swap: 0
    };
  }

  const ACTION_TYPES = {
    TRADE: 0,
    PENDING: 1,
    DEPOSIT: 2,
    CREDIT: 3
  };

  return transactions.reduce(
    (acc, t) => {
      console.log(acc, t)
      if (t.action === ACTION_TYPES.CREDIT) {
        acc.credit += t.profit || 0;
      }
      if (t.action === ACTION_TYPES.DEPOSIT && (t.profit || 0) > 0) {
        acc.deposit += t.profit;
      }
      if (t.action === ACTION_TYPES.DEPOSIT && (t.profit || 0) < 0) {
        acc.withdrawal += t.profit;
      }
      if (t.action === ACTION_TYPES.TRADE || t.action === ACTION_TYPES.PENDING) {
        acc.profit += t.profit || 0;
      }
      acc.balance += (t.profit || 0) + (t.commission || 0) + (t.swap || 0);
      acc.commission += t.commission || 0;
      acc.swap += t.swap || 0;
      return acc;
    },
    {
      credit: 0,
      deposit: 0,
      withdrawal: 0,
      profit: 0,
      balance: 0,
      commission: 0,
      swap: 0
    }
  );
}

export default useFinancialMetricsWorker;

